import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { AnalyticsService } from './analytics.service';
import { Attraction, AttractionsResponse, ATTRACTIONS_PAGE_SIZE } from '../models/attraction.model';

/**
 * Tourist attractions (FE2-T168), against Feather's own API.
 *
 * A thin pass-through, mirroring the Android `AttractionsRepository` + the web `ShopStatusService`
 * style: reuses `ApiService` (which already attaches `Authorization: Bearer` + `X-Client`), wraps each
 * call in `firstValueFrom(...).catch(() => null)`. Ordering, curation, currency and destination are
 * all server-owned — the catalogue arrives in rating order and `recommended` in the admin's order, so
 * nothing here re-sorts or truncates.
 *
 * Scoped by shop id (`clientData.id`), never by city name — the backend resolves the city itself.
 */
@Injectable({
  providedIn: 'root'
})
export class AttractionsService {

  constructor(
    private apiService: ApiService,
    private analytics: AnalyticsService
  ) {}

  /**
   * Every call here swallows its failure so the screen can show one generic string. That makes a
   * city with no attractions and a city whose catalogue is broken look identical — this event is
   * the only thing that tells them apart, so a whole city's Explore can't quietly stay dead.
   *
   * Reuses the already-registered `error_type` / `error_message` dimensions, so it costs no new
   * GA4 dimension slot.
   */
  private reportError(errorType: 'list' | 'recommended' | 'details', error: any): null {
    this.analytics.log('attractions_error', {
      error_type: errorType,
      // GA4 truncates parameter values at 100 characters; trim rather than let it happen silently.
      error_message: String(error?.status ?? error?.message ?? error ?? 'unknown').slice(0, 100)
    });
    return null;
  }

  /**
   * One page of the venue city's catalogue, in the provider's traveler-rating order.
   * `page` is 1-based; read `total_count` off the response for load-more (whole-catalogue size, not
   * this page). `limit` is capped at 50 server-side.
   */
  getAttractions(shopId: number | string, page = 1, limit = ATTRACTIONS_PAGE_SIZE): Promise<AttractionsResponse | null> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));
    return firstValueFrom(
      this.apiService.getWithParams<AttractionsResponse>(`api/v3/shops/${shopId}/attractions`, params)
    ).catch(err => this.reportError('list', err));
  }

  /**
   * The venue's curated picks, whole and already in the admin's order. Not paginated — the set is
   * bounded by what an admin chose. Do NOT re-sort or truncate the result.
   */
  getRecommended(shopId: number | string): Promise<AttractionsResponse | null> {
    return firstValueFrom(
      this.apiService.get<AttractionsResponse>(`api/v3/shops/${shopId}/attractions/recommended`)
    ).catch(err => this.reportError('recommended', err));
  }

  /**
   * Full content for one attraction — a superset of the list item. Call only when a details screen
   * opens, never in a loop over a list.
   */
  getAttraction(shopId: number | string, code: string): Promise<Attraction | null> {
    return firstValueFrom(
      this.apiService.get<Attraction>(`api/v3/shops/${shopId}/attractions/${code}`)
    ).catch(err => this.reportError('details', err));
  }
}
